import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { LeadInput } from './types';

export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (params: api.LeadListParams) => [...leadKeys.lists(), params] as const,
  detail: (id: string | number) => [...leadKeys.all, 'detail', id] as const,
};

export function useLeads(params: api.LeadListParams) {
  return useQuery({
    queryKey: leadKeys.list(params),
    queryFn: () => api.listLeads(params),
    placeholderData: keepPreviousData,
  });
}

export function useLead(id: string | number) {
  return useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: () => api.getLead(id),
    enabled: Boolean(id),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadInput) => api.createLead(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Lead created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateLead(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<LeadInput>) => api.updateLead(id, input),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      qc.setQueryData(leadKeys.detail(id), lead);
      toast.success('Lead updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.archiveLead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Lead archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.restoreLead(id),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      qc.setQueryData(leadKeys.detail(lead.public_id), lead);
      toast.success('Lead restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.purgeLead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      toast.success('Lead permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

/**
 * Convert this Lead to a Deal (server-side, audited C8).
 * Also invalidates Deals lists since a new deal was created.
 */
export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deal_name }: { id: string | number; deal_name?: string }) =>
      api.convertLead(id, { deal_name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: leadKeys.lists() });
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.success('Lead converted to a deal');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
